/**
 * RGM Studio — Collaborators drawer.
 * Owner: invite RGM artists by name/@username, see status, assign lanes, revoke.
 * Collaborator: read-only list of who is in the session.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArtistChip, StatusPill } from "./ArtistChip";
import { ArtistSearch } from "./ArtistSearch";

const LaneAssign = ({ collab, tracks, onSave }) => {
  const assigned = new Set(collab.authorized_track_ids || []);
  const lanes = (tracks || []).map((t, i) => ({ ...t, n: i + 1 })).filter((t) => (t.created_by || t.user_id) !== collab.user_id);
  if (!lanes.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1" data-testid={`collab-lanes-${collab.user_id}`}>
      {lanes.map((t) => (
        <button key={t.track_id} type="button"
          onClick={() => {
            const next = new Set(assigned);
            next.has(t.track_id) ? next.delete(t.track_id) : next.add(t.track_id);
            onSave([...next]);
          }}
          className={`text-[10px] px-2 py-1 rounded-full border ${assigned.has(t.track_id) ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-200" : "border-white/15 text-gray-400"}`}
          data-testid={`collab-lane-toggle-${collab.user_id}-${t.track_id}`}>
          {assigned.has(t.track_id) ? "✓ " : "+ "}Lane {t.n} · {t.label || "Vocal"}
        </button>
      ))}
    </div>
  );
};

export default function CollabDrawer({ open, onClose, projectId, isOwner, tracks }) {
  const [collabs, setCollabs] = useState(null);
  const [owner, setOwner] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get(`/studio/projects/${projectId}/collaborators`);
      setCollabs(r.data.collaborators || []);
      setOwner(r.data.owner || null);
    } catch { setCollabs([]); }
  };

  useEffect(() => { if (open && projectId) { setCollabs(null); reload(); } }, [open, projectId]);  // eslint-disable-line

  const invite = async (u) => {
    setBusy(true);
    try {
      await api.post(`/studio/projects/${projectId}/collaborators/invite`, { user_id: u.user_id });
      toast.success(`Invite sent to ${u.name}`);
      reload();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not invite");
      return false;
    } finally { setBusy(false); }
  };

  const revoke = async (c) => {
    if (!window.confirm(`Remove ${c.profile?.name || "this artist"}'s access? Their recorded takes stay in the project.`)) return;
    try {
      await api.delete(`/studio/projects/${projectId}/collaborators/${c.user_id}`);
      toast.success("Access revoked");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
  };

  const saveLanes = async (c, ids) => {
    try {
      await api.patch(`/studio/projects/${projectId}/collaborators/${c.user_id}`, { authorized_track_ids: ids });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update lanes"); }
  };

  if (!open) return null;
  const visible = (collabs || []).filter((c) => isOwner || c.status === "accepted");

  return (
    <>
      <div className="fixed inset-0 z-30 bg-black/40" onClick={onClose} data-testid="collab-scrim" />
      <aside className="fixed top-0 bottom-[76px] right-0 z-40 w-[22rem] max-w-[92vw] bg-[#0a0a10] border-l border-white/10 shadow-[-8px_0_24px_rgba(0,0,0,0.55)] flex flex-col"
        data-testid="studio-collab-drawer" role="dialog" aria-label="Collaborators">
        <header className="flex items-center gap-2 px-4 h-12 border-b border-white/10">
          <p className="text-xs font-bold text-white/90 flex-1">Collaborators</p>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white text-xs" data-testid="collab-close-btn">Close ✕</button>
        </header>
        <div className="p-4 flex-1 overflow-y-auto space-y-5">
          {isOwner ? (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2">Invite RGM artist</p>
              <ArtistSearch projectId={projectId} onPick={invite} busy={busy} />
            </div>
          ) : (
            <p className="text-[11px] text-gray-400" data-testid="collab-non-owner">Only the project owner can invite or remove collaborators.</p>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2">In this session</p>
            {owner && (
              <div className="flex items-center gap-2 p-2 mb-1 rounded-lg bg-black/30 border border-white/10" data-testid="collab-owner-row">
                <span className="flex-1 min-w-0"><ArtistChip profile={owner} /></span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-200 font-bold">Owner</span>
              </div>
            )}
            {collabs === null ? (
              <p className="text-[11px] text-gray-500" data-testid="collab-loading">Loading…</p>
            ) : visible.length === 0 ? (
              <p className="text-[11px] text-gray-500" data-testid="collab-empty">No collaborators yet.</p>
            ) : (
              <ul className="space-y-1" data-testid="collab-list">
                {visible.map((c) => (
                  <li key={c.user_id} className="p-2 rounded-lg bg-black/30 border border-white/10" data-testid={`collab-row-${c.user_id}`}>
                    <div className="flex items-center gap-2">
                      <span className="flex-1 min-w-0"><ArtistChip profile={c.profile} /></span>
                      <StatusPill status={c.status} testId={`collab-status-${c.user_id}`} />
                      {isOwner && (c.status === "pending" || c.status === "accepted") && (
                        <button onClick={() => revoke(c)} className="text-[10px] text-red-300 hover:text-red-200"
                          data-testid={`collab-revoke-${c.user_id}`}>{c.status === "pending" ? "Cancel" : "Revoke"}</button>
                      )}
                    </div>
                    {isOwner && c.status === "accepted" && <LaneAssign collab={c} tracks={tracks} onSave={(ids) => saveLanes(c, ids)} />}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[10px] text-gray-500 mt-3">
              Collaborators record on their own vocal lanes (or lanes you assign). Your beat and recordings stay protected. Each artist controls their own takes.
            </p>
          </div>
        </div>
      </aside>
    </>
  );
}
